import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Fire, Basketball, Buildings, CreditCard, WhatsappLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const ADMIN_WHATSAPP = "5521984317457";

const RENTAL_ITEMS = [
  {
    id: "churrasqueira",
    title: "Aluguel de Churrasqueira",
    description: "Área completa de churrasco com mesa, cadeiras e utensílios",
    price: 150,
    icon: Fire,
    color: "from-[#FF5A26] to-[#E04818]",
    features: ["Churrasqueira completa", "Mesas e cadeiras", "Área coberta", "Uso de utensílios"]
  },
  {
    id: "quadra_festa",
    title: "Aluguel de Quadra para Festa",
    description: "Quadra esportiva reservada exclusivamente para seu evento",
    price: 600,
    icon: Basketball,
    color: "from-green-500 to-emerald-600",
    features: ["Quadra exclusiva", "Iluminação", "Espaço amplo", "Ideal para festas"]
  },
  {
    id: "clube_todo",
    title: "Aluguel do Clube Todo",
    description: "O MARABU OPEN CLUB inteiro reservado para seu evento exclusivo",
    price: 6000,
    icon: Buildings,
    color: "from-blue-600 to-indigo-700",
    features: ["Piscina", "Churrasqueira", "Quadra", "Salão completo", "Estacionamento"]
  }
];

const RentalCard = ({ item, onPay, onWhatsApp }) => {
  const IconComp = item.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-[#E4DFD3] rounded-2xl overflow-hidden shadow-sm"
      data-testid={`rental-card-${item.id}`}
    >
      <div className={`bg-gradient-to-br ${item.color} p-6 text-white`}>
        <IconComp size={36} weight="duotone" className="mb-3" />
        <h3 className="text-xl font-black">{item.title}</h3>
        <p className="text-white/80 text-sm mt-1">{item.description}</p>
      </div>

      <div className="p-5">
        <div className="flex flex-wrap gap-2 mb-4">
          {item.features.map((f, i) => (
            <span key={i} className="text-xs bg-[#F4F1EA] text-[#115E59] font-semibold px-3 py-1.5 rounded-full">
              {f}
            </span>
          ))}
        </div>

        <div className="text-3xl font-black text-[#042F2E] mb-5">
          R$ {item.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
        </div>

        <div className="space-y-2">
          <Button
            onClick={() => onPay(item)}
            className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-5 text-sm"
            data-testid={`pay-rental-${item.id}`}
          >
            <CreditCard size={18} weight="bold" className="mr-2" />
            Pagar (PIX, Débito ou Crédito)
          </Button>
          <Button
            onClick={() => onWhatsApp(item)}
            variant="outline"
            className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white font-bold rounded-full py-5 text-sm"
            data-testid={`whatsapp-rental-${item.id}`}
          >
            <WhatsappLogo size={18} weight="fill" className="mr-2" />
            Reservar via WhatsApp
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

const Rentals = () => {
  const navigate = useNavigate();

  const handlePay = (item) => {
    navigate("/pix-payment", {
      state: {
        itemId: item.id,
        itemType: "product",
        itemInfo: { title: item.title, description: "Aluguel", price: item.price }
      }
    });
  };

  const handleWhatsApp = (item) => {
    const msg = `Olá! Gostaria de reservar o *${item.title}* no MARABU OPEN CLUB.\n\nValor: R$ ${item.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}\n\nPor favor, me ajude com a reserva!`;
    window.open(`https://wa.me/${ADMIN_WHATSAPP}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div data-testid="rentals-tab">
      <h2 className="text-2xl sm:text-3xl font-bold text-[#042F2E] mb-2">Aluguéis</h2>
      <p className="text-[#115E59] mb-8">Reserve espaços do MARABU para seu evento</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {RENTAL_ITEMS.map((item) => (
          <RentalCard key={item.id} item={item} onPay={handlePay} onWhatsApp={handleWhatsApp} />
        ))}
      </div>
    </div>
  );
};

export default Rentals;
